import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Loader2 } from 'lucide-react';
import { Button, Alert, Spinner } from '../ui/components';
import { useGetConnectedProvidersQuery, useDisconnectProviderMutation } from '../services/externalAuthApi';
import { useAppSelector } from '../store/hooks';

const tabs = ['Exchange Connections'];

const SettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [provider, setProvider] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  const { data: connectedProviders, isLoading: isLoadingProviders, refetch: refetchProviders } = 
    useGetConnectedProvidersQuery(undefined, { skip: !isAuthenticated });
  const [disconnectProvider, { isLoading: isDisconnecting }] = useDisconnectProviderMutation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauthSuccess = params.get('oauth_success');
    const providerParam = params.get('provider');

    if (oauthSuccess && providerParam) {
      setProvider(providerParam);
      if (oauthSuccess === 'true') {
        setShowSuccessAlert(true);
      } else {
        setShowErrorAlert(true);
      }
      navigate('/settings', { replace: true });
      refetchProviders();
    }
  }, [location, navigate, refetchProviders]);

  const handleConnect = (providerName) => {
    const returnUrl = `${window.location.origin}/settings`;
    window.location.href = `/api/external-auth/oauth?provider=${providerName}&returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  const handleDisconnect = async (providerName) => {
    try {
      await disconnectProvider(providerName).unwrap();
      refetchProviders();
    } catch (error) {
      console.error('Failed to disconnect provider:', error);
    }
  };

  const isProviderConnected = (providerName) => {
    return connectedProviders?.includes(providerName) || false;
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 16, color: 'var(--ds-text-primary)' }}>Settings</h1>

      {showSuccessAlert && (
        <div style={{ padding: '12px 16px', marginBottom: 16, borderRadius: 'var(--ds-border-radius-medium)', backgroundColor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#16a34a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Successfully connected to {provider}!</span>
          <button onClick={() => setShowSuccessAlert(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>&times;</button>
        </div>
      )}

      {showErrorAlert && (
        <div style={{ padding: '12px 16px', marginBottom: 16, borderRadius: 'var(--ds-border-radius-medium)', backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#dc2626', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Failed to connect to {provider}. Please try again.</span>
          <button onClick={() => setShowErrorAlert(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>&times;</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
        {/* Sidebar tabs */}
        <div style={{ width: 220, flexShrink: 0, borderRight: '1px solid var(--ds-border-default)' }}>
          {tabs.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setTabValue(i)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px',
                background: tabValue === i ? 'var(--ds-bg-secondary)' : 'none',
                border: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: tabValue === i ? 600 : 400,
                color: tabValue === i ? 'var(--ds-text-primary)' : 'var(--ds-text-secondary)',
                borderRadius: 'var(--ds-border-radius-medium)',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: '0 16px' }}>
          {tabValue === 0 && (
            <>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: 8, color: 'var(--ds-text-primary)' }}>Exchange Connections</h2>
              <p style={{ color: 'var(--ds-text-secondary)', marginBottom: 24 }}>
                Connect your cryptocurrency exchange accounts to enable automated trading and portfolio tracking.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                <div style={{ width: 280, border: '1px solid var(--ds-border-default)', borderRadius: 'var(--ds-border-radius-large)', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <img src="/assets/coinbase-logo.svg" alt="Coinbase Logo" style={{ height: 60, marginBottom: 16 }} />
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 8, color: 'var(--ds-text-primary)' }}>Coinbase</h3>
                  <p style={{ fontSize: '0.875rem', color: 'var(--ds-text-secondary)', textAlign: 'center', marginBottom: 16 }}>
                    Connect your Coinbase account to enable automated trading.
                  </p>
                  <div style={{ marginTop: 'auto', textAlign: 'center' }}>
                    {isLoadingProviders ? (
                      <Loader2 size={24} className="animate-spin" />
                    ) : isProviderConnected('Coinbase') ? (
                      <>
                        <span style={{ fontSize: '0.875rem', color: '#16a34a', display: 'block', marginBottom: 8 }}>Connected</span>
                        <Button variant="destructive" size="sm" onClick={() => handleDisconnect('Coinbase')} disabled={isDisconnecting}>
                          {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                        </Button>
                      </>
                    ) : (
                      <Button variant="primary" onClick={() => handleConnect('Coinbase')}>Connect</Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
